import { useState, useRef, useCallback, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

type ToolbarAction =
  | 'bold' | 'italic' | 'underline' | 'strikethrough'
  | 'h2' | 'h3' | 'quote' | 'ul' | 'ol'
  | 'link' | 'image' | 'code';

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Escreva o conteúdo do artigo aqui...',
  className = '',
}: RichTextEditorProps) {
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value);
  const editorRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Sync external value changes into the editor
  useEffect(() => {
    if (!editorRef.current) return;
    if (!initializedRef.current && value) {
      editorRef.current.innerHTML = value;
      initializedRef.current = true;
    }
  }, [value]);

  // Update htmlSource when switching to HTML mode
  useEffect(() => {
    if (htmlMode && editorRef.current) {
      setHtmlSource(editorRef.current.innerHTML);
    }
  }, [htmlMode]);

  const exec = useCallback((action: ToolbarAction) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();

    if (action === 'bold') {
      document.execCommand('bold', false);
    } else if (action === 'italic') {
      document.execCommand('italic', false);
    } else if (action === 'underline') {
      document.execCommand('underline', false);
    } else if (action === 'strikethrough') {
      document.execCommand('strikeThrough', false);
    } else if (action === 'h2') {
      document.execCommand('formatBlock', false, 'h2');
    } else if (action === 'h3') {
      document.execCommand('formatBlock', false, 'h3');
    } else if (action === 'quote') {
      document.execCommand('formatBlock', false, 'blockquote');
    } else if (action === 'ul') {
      document.execCommand('insertUnorderedList', false);
    } else if (action === 'ol') {
      document.execCommand('insertOrderedList', false);
    } else if (action === 'link') {
      const url = prompt('URL do link:');
      if (url) document.execCommand('createLink', false, url);
    } else if (action === 'image') {
      const url = prompt('URL da imagem:');
      if (url) document.execCommand('insertImage', false, url);
    } else if (action === 'code') {
      document.execCommand('formatBlock', false, 'pre');
    }
  }, []);

  const handleVisualInput = useCallback(() => {
    if (!editorRef.current) return;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const handleHtmlChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newHtml = e.target.value;
      setHtmlSource(newHtml);
      onChange(newHtml);
    },
    [onChange],
  );

  const toggleMode = useCallback(() => {
    if (htmlMode) {
      // Switching back to visual: inject the HTML source into the editor
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlSource;
        initializedRef.current = true;
      }
    } else {
      // Switching to HTML: grab current content
      if (editorRef.current) {
        setHtmlSource(editorRef.current.innerHTML);
      }
    }
    setHtmlMode(prev => !prev);
  }, [htmlMode, htmlSource]);

  const toolbarButtons: { action: ToolbarAction; icon: string; label: string }[] = [
    { action: 'bold', icon: 'ri-bold', label: 'Negrito' },
    { action: 'italic', icon: 'ri-italic', label: 'Itálico' },
    { action: 'underline', icon: 'ri-underline', label: 'Sublinhado' },
    { action: 'h2', icon: 'ri-h-2', label: 'Título H2' },
    { action: 'h3', icon: 'ri-h-3', label: 'Subtítulo H3' },
    { action: 'quote', icon: 'ri-double-quotes-l', label: 'Citação' },
    { action: 'ul', icon: 'ri-list-unordered', label: 'Lista' },
    { action: 'ol', icon: 'ri-list-ordered', label: 'Lista numerada' },
    { action: 'link', icon: 'ri-link', label: 'Link' },
    { action: 'image', icon: 'ri-image-add-line', label: 'Imagem' },
  ];

  return (
    <div className={`border border-background-200 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-background-100 border-b border-background-200 flex-wrap">
        {toolbarButtons.map(btn => (
          <button
            key={btn.action}
            type="button"
            onClick={() => exec(btn.action)}
            disabled={htmlMode}
            title={btn.label}
            className="w-8 h-8 flex items-center justify-center rounded text-foreground-500 hover:bg-background-200 hover:text-foreground-800 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <i className={`${btn.icon} text-sm`}></i>
          </button>
        ))}

        {/* Separator */}
        <div className="w-px h-5 bg-background-300 mx-1" />

        {/* HTML Toggle */}
        <button
          type="button"
          onClick={toggleMode}
          className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
            htmlMode
              ? 'bg-primary-500 text-white'
              : 'bg-background-200 text-foreground-600 hover:bg-background-300'
          }`}
        >
          <i className="ri-code-s-slash-line text-sm"></i>
          HTML
        </button>
      </div>

      {/* Editor Area */}
      {htmlMode ? (
        <textarea
          value={htmlSource}
          onChange={handleHtmlChange}
          placeholder={placeholder}
          className="w-full min-h-[400px] px-4 py-3 text-sm font-mono bg-white text-foreground-900 resize-none focus:outline-none"
          spellCheck={false}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleVisualInput}
          data-placeholder={placeholder}
          className="rich-text-editor w-full min-h-[400px] px-4 py-3 text-sm bg-white text-foreground-900 focus:outline-none cursor-text prose-custom"
        />
      )}

      <style>{`
        .rich-text-editor[data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor:focus {
          outline: none;
        }
        .rich-text-editor h2 {
          font-family: var(--font-heading, inherit);
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .rich-text-editor h3 {
          font-family: var(--font-heading, inherit);
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }
        .rich-text-editor p {
          margin-bottom: 0.75rem;
          line-height: 1.7;
        }
        .rich-text-editor ul,
        .rich-text-editor ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .rich-text-editor ul {
          list-style-type: disc;
        }
        .rich-text-editor ol {
          list-style-type: decimal;
        }
        .rich-text-editor li {
          margin-bottom: 0.25rem;
        }
        .rich-text-editor blockquote {
          border-left: 4px solid var(--primary-500, #e5e7eb);
          padding: 0.5rem 1rem;
          margin: 1rem 0;
          font-style: italic;
          background: oklch(var(--background-100, 0.97 0.01 100) / 0.7);
          border-radius: 0 0.5rem 0.5rem 0;
        }
        .rich-text-editor a {
          color: oklch(var(--primary-500));
          text-decoration: underline;
        }
        .rich-text-editor img {
          max-width: 100%;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        .rich-text-editor pre {
          background: oklch(var(--background-100));
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
          font-family: monospace;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}